import React, { useState } from 'react';
import { X, Bookmark, Save, CheckCircle, XCircle } from 'lucide-react';
import { subjectColors } from '../utils/constants';
interface ArchiveQuestionDetailProps {
  question: {
    id: number;
    subject: string;
    topic: string;
    subtopic: string;
    difficulty: string;
    solved: boolean;
    date: string;
    archived: boolean;
    content?: string;
    options?: string[];
    correctOption?: number;
    image?: string;
    notes?: string;
  };
  onClose: () => void;
  onToggleArchive: (questionId: number) => void;
  onSaveNotes?: (questionId: number, notes: string) => void;
  subjectId: string;
}
export function ArchiveQuestionDetail({
  question,
  onClose,
  onToggleArchive,
  onSaveNotes,
  subjectId
}: ArchiveQuestionDetailProps) {
  const [isArchived, setIsArchived] = useState(question.archived);
  const [notes, setNotes] = useState(question.notes || '');
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [showSolution, setShowSolution] = useState(false);
  const gradient = subjectColors[subjectId as keyof typeof subjectColors] || {
    from: 'from-purple-600',
    to: 'to-indigo-600'
  };
  const handleToggleArchive = () => {
    setIsArchived(!isArchived);
    onToggleArchive(question.id);
    // If we're unarchiving, close the detail view
    if (isArchived) {
      onClose();
    }
  };
  const handleSaveNotes = () => {
    if (onSaveNotes) {
      onSaveNotes(question.id, notes);
    }
  };
  const handleOptionSelect = (index: number) => {
    setSelectedOption(index);
    setShowSolution(true);
  };
  // Option letter mapping
  const optionLetters = ['A', 'B', 'C', 'D', 'E', 'F'];
  return <div className="fixed inset-0 z-50 overflow-y-auto bg-black/75" data-id="element-126">
      <div className="min-h-screen px-4 py-8" data-id="element-127">
        <div className="max-w-4xl mx-auto space-y-4" data-id="element-128">
          <div className={`relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r ${gradient.from} ${gradient.to}`} data-id="element-129">
            <div className="relative p-6 bg-black/90 rounded-xl" data-id="element-130">
              <div className="flex justify-between items-start mb-6" data-id="element-131">
                <div data-id="element-132">
                  <div className="flex items-center gap-4 mb-2" data-id="element-133">
                    <span className={`px-3 py-1 rounded-full text-xs ${question.difficulty === 'Zor' ? 'bg-red-400/10 text-red-400' : question.difficulty === 'Orta' ? 'bg-yellow-400/10 text-yellow-400' : 'bg-green-400/10 text-green-400'}`} data-id="element-134">
                      {question.difficulty}
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold" data-id="element-135">{question.subtopic}</h2>
                  <p className="text-gray-400 mt-1" data-id="element-136">
                    {question.subject} - {question.topic}
                  </p>
                </div>
                <div className="flex items-center gap-4" data-id="element-137">
                  <button onClick={handleToggleArchive} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${isArchived ? 'bg-purple-500/20 text-purple-400' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`} data-id="element-138">
                    <Bookmark className="w-4 h-4" fill={isArchived ? 'currentColor' : 'none'} data-id="element-139" />
                    <span data-id="element-140">{isArchived ? 'Kaldır' : 'Kaydet'}</span>
                  </button>
                  <button onClick={onClose} className="text-gray-400 hover:text-white" data-id="element-141">
                    <X className="w-6 h-6" data-id="element-142" />
                  </button>
                </div>
              </div>
              <div className="space-y-6" data-id="element-143">
                <div className="prose prose-invert max-w-none" data-id="element-144">
                  <p className="text-gray-200" data-id="element-145">
                    {question.content || '[Soru içeriği buraya gelecek]'}
                  </p>
                </div>
                {question.image && <img src={question.image} alt="Soru" className="rounded-lg w-full" data-id="element-146" />}
                {question.options && question.options.length > 0 && <div className="space-y-3 mt-4" data-id="element-147">
                    <h3 className="text-lg font-medium" data-id="element-148">Şıklar</h3>
                    {question.options.map((option, index) => <button key={index} onClick={() => handleOptionSelect(index)} className={`w-full p-4 rounded-lg text-left transition-colors flex items-center gap-4 ${selectedOption === index ? showSolution ? question.correctOption !== undefined && index === question.correctOption ? 'bg-green-500/20 text-green-400' : selectedOption === index ? 'bg-red-500/20 text-red-400' : 'bg-white/5' : 'bg-purple-500/20 text-purple-400' : 'bg-white/5 hover:bg-white/10'}`} disabled={showSolution} data-id="element-149">
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-lg font-medium border ${selectedOption === index ? showSolution ? question.correctOption !== undefined && index === question.correctOption ? 'border-green-500 bg-green-500/20 text-green-400' : selectedOption === index ? 'border-red-500 bg-red-500/20 text-red-400' : 'border-gray-600 bg-black/60 text-gray-400' : 'border-purple-500 bg-purple-500/20 text-purple-400' : 'border-gray-600 bg-black/60 text-gray-400'}`} data-id="element-150">
                          {optionLetters[index]}
                        </div>
                        <span className="flex-1" data-id="element-151">{option}</span>
                        {showSolution && <>
                            {question.correctOption !== undefined && index === question.correctOption && <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-152" />}
                            {selectedOption === index && question.correctOption !== undefined && selectedOption !== question.correctOption && <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-153" />}
                          </>}
                      </button>)}
                  </div>}
                <div className="space-y-3 mt-6" data-id="element-154">
                  <h3 className="text-lg font-medium" data-id="element-155">Notlar</h3>
                  <div className="relative" data-id="element-156">
                    <textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full bg-white/5 text-white rounded-lg px-4 py-3 min-h-[100px] focus:ring-2 ring-purple-500/20" placeholder="Bu soru hakkında notlarınızı buraya ekleyin..." data-id="element-157" />
                    <button onClick={handleSaveNotes} className="absolute bottom-3 right-3 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" data-id="element-158">
                      <Save className="w-4 h-4 text-purple-400" data-id="element-159" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}